import { get, has, isEmpty, set } from 'lodash-es';
import {
  type ProviderProps,
  createContext,
  createElement,
  useContext,
  useEffect,
} from 'react';

const VUERON_KISKA_DEV_KEY = '__VUERON_KISKA_DEV__';

if (import.meta.env.DEV) {
  if (!has(window, VUERON_KISKA_DEV_KEY)) {
    set(window, VUERON_KISKA_DEV_KEY, {
      SAFE_CONTEXT_MAP: {},
    });
  }
}

const createSafeContext = <CtxValue extends Record<string, any>>(
  defaultValue: Nullish<CtxValue> = {} as CtxValue,
  contextKey?: string,
) => {
  const context = createContext<CtxValue>(defaultValue as CtxValue);

  const useSafeContext = <T = CtxValue>() => {
    let ctx = useContext<T>(context as any);

    if (import.meta.env.DEV && isEmpty(ctx)) {
      ctx = get(
        window,
        `${VUERON_KISKA_DEV_KEY}.SAFE_CONTEXT_MAP.${contextKey}`,
      ) as T;
    }

    if (!ctx) {
      throw new Error(
        'useContext error: component must be inside a Provider with a value',
      );
    }

    if (import.meta.env.DEV && contextKey) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        set(
          window,
          `${VUERON_KISKA_DEV_KEY}.SAFE_CONTEXT_MAP.${contextKey}`,
          ctx,
        );
      }, [ctx]);
    }

    return ctx;
  };

  return {
    hook: <T = CtxValue>() => (useSafeContext as any)() as T,
    Provider: <T = CtxValue>(props: ProviderProps<T>) =>
      createElement((context as unknown as React.Context<T>).Provider, props),
  };
};

export default createSafeContext;
